
import React, { useEffect, useMemo, useState } from "react";
import "../styles/FreeTextTruckModal.css";
import { useTranslation } from "react-i18next";
/**
 * Simple free-text modal for manual truck label assignment.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onSubmit: async (text: string) => Promise<any> | any
 * - title?: string
 * - initialValue?: string
 * - maxLen?: number (default 15)
 * - lettersOnly?: boolean (default true)
 * - placeholder?: string
 */
export default function FreeTextTruckModal({
  isOpen,
  onClose,
  onSubmit,
  title,
  initialValue = "",
  maxLen = 15,
  lettersOnly = true,
  placeholder,
}) {
  const { t } = useTranslation("free_text_truck_modal");
  const [text, setText] = useState(initialValue || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const computedTitle =
    title ||
    t("title", { defaultValue: "Manual truck label" });
  const computedPlaceholder =
    placeholder ||
    t("placeholder", { defaultValue: "Type up to 15 characters..." });
  const allowedRegex = useMemo(() => {
    // lettersOnly=true => allow letters + space + hyphen
    // lettersOnly=false => allow letters/numbers + space + hyphen
    // ✅ allow unicode letters (accents), spaces, hyphen
    // lettersOnly=true => letters + space + hyphen
    // lettersOnly=false => letters + numbers + space + hyphen
    return lettersOnly
      ? /[^\p{L}\s-]/gu
      : /[^\p{L}\p{N}\s-]/gu;
  }, [lettersOnly]);
  // Reset when opening (and when initialValue changes)
  useEffect(() => {
    if (!isOpen) return;
    setText(initialValue || "");
    setError("");
    setSaving(false);
  }, [isOpen, initialValue]);
  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const cleaned = (raw) => {
    if (raw == null) return "";
    // strip disallowed chars
    let v = String(raw).replace(allowedRegex, "");
    // trim + collapse multiple spaces
    v = v.replace(/\s+/g, " ").trimStart();
    // enforce max length
    if (v.length > maxLen) v = v.slice(0, maxLen);
    return v;
  };
  const validate = (v) => {
    const vv = String(v || "").trim();
    if (!vv) return t("errors.required", { defaultValue: "Field is required." });
    if (vv.length > maxLen)
      return t("errors.max", { defaultValue: `Max ${maxLen} characters.` }, { maxLen });
    return "";
  };
  const remaining = maxLen - (text?.length || 0);
  const currentError = error || validate(text);
  const handleSave = async () => {
    const v = String(text || "").trim();
    const vErr = validate(v);
    if (vErr) {
      setError(vErr);
      return;
    }
    try {
      setSaving(true);
      setError("");
      await Promise.resolve(onSubmit?.(v));
      onClose?.();
    } catch (e) {
      console.error("[FreeTextTruckModal] submit error", e);
      setError(
        t("errors.submit", {
          defaultValue: "Could not save. Please try again.",
        })
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="free-text-modal-overlay" onMouseDown={onClose}>
      <div
        className="free-text-modal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="free-text-modal-header">
          <h2 className="free-text-modal-title">{computedTitle}</h2>
          <button
            className="free-text-modal-x"
            onClick={onClose}
            aria-label={t("aria.close", { defaultValue: "Close" })}
          >
            ×
          </button>
        </div>
        <div className="free-text-modal-body">
          <label className="free-text-label">
            {t("label", { defaultValue: "Truck label" })}
          </label>
          <input
            className={`free-text-input ${currentError ? "input-error" : ""}`}
            value={text}
            placeholder={computedPlaceholder}
            onChange={(e) => {
              const v = cleaned(e.target.value);
              setText(v);
              if (error) setError("");
            }}
            maxLength={maxLen}
            autoFocus
          />
          <div className="free-text-meta">
            <span className="free-text-counter">
              {t("counter", {
                defaultValue: "{{remaining}} left",
                remaining,
              })}
            </span>
            <span className="free-text-hint">
              {lettersOnly
                ? t("hint.lettersOnly", { defaultValue: "Letters only" })
                : t("hint.alnum", { defaultValue: "Letters / numbers allowed" })}
            </span>
          </div>
          {currentError ? (
            <div className="free-text-error">{currentError}</div>
          ) : null}
        </div>
        <div className="free-text-modal-actions">
          <button className="free-text-btn ghost" onClick={onClose} disabled={saving}>
            {t("buttons.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            className="free-text-btn primary"
            onClick={handleSave}
            disabled={saving || !!validate(text)}
          >
            {saving
              ? t("buttons.saving", { defaultValue: "Saving..." })
              : t("buttons.save", { defaultValue: "Save" })}
          </button>
        </div>
      </div>
    </div>
  );
}
